import { useApolloClient } from '@apollo/client'
import { useFeatureGate } from '@statsig/react-bindings'
import { type NextPage } from 'next'
import { useRouter } from 'next/router'
import queryString from 'query-string'
import React, { useEffect, useRef, useCallback } from 'react'
import { EXPERT_FEATURES, EXPERT_USER_CAPABILITIES } from 'shared-constants'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { Button, BUTTON_VARIANT } from 'components/Button'
import { Callout, CALLOUT_INTENT } from 'components/Callout'
import Icon from 'components/Icon'
import { StyledLink } from 'components/Link'
import { SideNavPage } from 'components/SideNavPage'
import { SignedInAsUserHeader } from 'components/SignedInAsUserHeader'
import { Text } from 'components/Typography'
import { ZOOM_ACCOUNT_TYPE } from 'constants/common'
import { environment } from 'constants/environment'
import { LIST_OF_PAGES_WITH_FULL_VIEWPORT, ROUTE } from 'constants/routes'
import { withExpertAuthorization } from 'hoc/withAuthorization'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import { useShouldRenderExpertContent } from 'hooks'
import { GA4_PAGE_AREAS } from 'providers/googleAnalytics'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_INTENT, FONT_SIZE, FONT_WEIGHT } from 'Theme'
import {
  ExpertMeWithStripeOnboardingTokenDocument,
  useCreateAccountLinkMutation,
} from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import { formatUserNameOrEmail } from 'utils/formatters'
import { ExpertContractModal } from '../ExpertContractModal'

const LinkText = styled(Text)`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const TIMEOUT_DELAY_IN_MILLISECONDS = 5000

const ExpertSideNavPage: NextPage<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    user,
    signedInUserType,
    signOut,
    isAdminLoggedInAsExpert,
    refetchAndSetUserData,
  } = useUserAuth()
  const apolloClient = useApolloClient()
  const router = useRouter()
  const media = useMedia()
  const timeoutRef = useRef(null)
  const {
    isMain,
    isZoomAccountActivated,
    zoomAccountType,
    isConsultationsCapable,
    expert: {
      slug,
      isStripeOnboarded,
      isStripeAccountActivated,
      isContractAccepted,
      capabilities,
    },
  } = user
  const { shouldRenderExpertContent } = useShouldRenderExpertContent()

  const [
    executeCreateAccountLinkMutation,
    { loading: isCreateAccountLinkLoading },
  ] = useCreateAccountLinkMutation()

  const showFavoritesButtonInPDP = useFeatureGate(
    'show_favorites_button_in_pdp'
  )

  const stopTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    const startRecursiveTimeout = () => {
      timeoutRef.current = setTimeout(async () => {
        await refetchAndSetUserData()
        startRecursiveTimeout()
      }, TIMEOUT_DELAY_IN_MILLISECONDS)
    }
    if (!isStripeAccountActivated && !timeoutRef.current) {
      startRecursiveTimeout()
    }
    if (isStripeAccountActivated && timeoutRef.current) {
      stopTimeout()
    }
    return stopTimeout
  }, [isStripeAccountActivated, refetchAndSetUserData, stopTimeout])

  const mobileFontSize = {
    ...(media.MOBILE ? { fontSize: FONT_SIZE.PX_12 } : {}),
  }

  const isStripeActivated = isStripeAccountActivated && isStripeOnboarded

  if (!shouldRenderExpertContent(EXPERT_FEATURES.EXPERT_DASHBOARD)) {
    return null
  }

  const isExpertConsultationsCapable = capabilities.includes(
    EXPERT_USER_CAPABILITIES.CONSULTATION
  )

  const shouldRenderExpertProfileGeneral = shouldRenderExpertContent(
    EXPERT_FEATURES.EXPERT_DASHBOARD.EXPERT_PROFILE.GENERAL
  )

  const shouldRenderExpertProfileShowroom = shouldRenderExpertContent(
    EXPERT_FEATURES.EXPERT_DASHBOARD.EXPERT_PROFILE.SHOWROOM
  )

  if (
    !isContractAccepted &&
    !isAdminLoggedInAsExpert &&
    isExpertConsultationsCapable
  ) {
    return <ExpertContractModal />
  }

  return (
    <SideNavPage
      footerText={formatUserNameOrEmail({ user, signedInUserType })}
      navigation={(menuRef) => (
        <SideNavPage.NavigationWrapper
          ref={menuRef}
          data-test-id={DATA_TEST_ID.EXPERT_DASHBOARD_SIDE_NAV}
        >
          {shouldRenderExpertContent(EXPERT_FEATURES.EXPERT_DASHBOARD.HOME) && (
            <SideNavPage.NavLink
              href={ROUTE.EXPERT_DASHBOARD()}
              label="Home"
              icon={Icon.Home}
            />
          )}
          {shouldRenderExpertContent(
            EXPERT_FEATURES.EXPERT_DASHBOARD.SCHEDULE
          ) && (
            <SideNavPage.NavLinkWrapper label="Schedule" icon={Icon.Calendar}>
              <SideNavPage.NavLink
                href={ROUTE.EXPERT_CALENDAR_UPCOMING()}
                label="Upcoming"
              />
              <SideNavPage.NavLink
                href={ROUTE.EXPERT_CALENDAR_PAST()}
                label="Past"
              />
              <SideNavPage.NavLink
                href={ROUTE.EXPERT_CALENDAR_AVAILABILITY()}
                label="Availability"
              />
            </SideNavPage.NavLinkWrapper>
          )}
          {shouldRenderExpertContent(
            EXPERT_FEATURES.EXPERT_DASHBOARD.CONSULTATIONS
          ) && (
            <SideNavPage.NavLink
              href={ROUTE.EXPERT_SESSIONS()}
              label="Consultations"
              icon={Icon.CommentPlay}
            />
          )}
          {shouldRenderExpertContent(
            EXPERT_FEATURES.EXPERT_DASHBOARD.CLIENTS
          ) && (
            <SideNavPage.NavLink
              href={ROUTE.EXPERT_CLIENTS()}
              label="Clients"
              icon={Icon.Website}
            />
          )}
          {shouldRenderExpertContent(
            EXPERT_FEATURES.EXPERT_DASHBOARD.COMMISSIONS
          ) && (
            <SideNavPage.NavLink
              href={ROUTE.EXPERT_COMMISSIONS()}
              label="Commissions"
              icon={Icon.DollarOutlined}
            />
          )}
          <SideNavPage.NavLink
            href={ROUTE.EXPERT_ORDERS()}
            label="Orders"
            icon={Icon.Basket}
          />
          {/* Temporarily disable vendors page https://github.com/sudolabs-io/the-expert/issues/2104 */}
          {/*           <SideNavPage.NavLink
            icon={Icon.Vendor}
            href={ROUTE.EXPERT_VENDORS()}
            label="Vendors"
          /> */}
          {showFavoritesButtonInPDP.value &&
            shouldRenderExpertContent(
              EXPERT_FEATURES.EXPERT_DASHBOARD.FAVORITES
            ) && (
              <SideNavPage.NavLink
                href={ROUTE.EXPERT_FAVORITES()}
                label="Favorites"
                icon={Icon.DashboardHeart}
              />
            )}
          {shouldRenderExpertContent(EXPERT_FEATURES.EXPERT_DASHBOARD.TEAM) && (
            <SideNavPage.NavLink
              href={ROUTE.EXPERT_TEAM()}
              label="Team"
              icon={Icon.Group}
            />
          )}
          {(shouldRenderExpertProfileGeneral ||
            shouldRenderExpertProfileShowroom) && (
            <SideNavPage.NavLinkWrapper label="Expert profile" icon={Icon.User}>
              {shouldRenderExpertProfileGeneral && (
                <SideNavPage.NavLink
                  href={ROUTE.EXPERT_SETTINGS_PROFILE()}
                  label="General"
                />
              )}
              {shouldRenderExpertProfileShowroom && (
                <SideNavPage.NavLink
                  href={ROUTE.EXPERT_SETTINGS_SHOWROOM()}
                  label="Showroom"
                />
              )}
            </SideNavPage.NavLinkWrapper>
          )}
          <SideNavPage.NavLinkWrapper label="Payment" icon={Icon.CardOutlined}>
            {shouldRenderExpertContent(
              EXPERT_FEATURES.EXPERT_DASHBOARD.PAYMENT.OVERVIEW
            ) && (
              <SideNavPage.NavLink
                href={ROUTE.EXPERT_SETTINGS_PAYMENT_OVERVIEW()}
                label="Overview"
              />
            )}
            <SideNavPage.NavLink
              href={ROUTE.EXPERT_SETTINGS_PAYMENT_BILLING()}
              label="Cards & Billing"
            />
            {shouldRenderExpertContent(
              EXPERT_FEATURES.EXPERT_DASHBOARD.PAYMENT.TAX_DOCUMENTS
            ) && (
              <SideNavPage.NavLink
                href={ROUTE.EXPERT_SETTINGS_TAX_DOCUMENTS()}
                label="Tax documents"
              />
            )}
          </SideNavPage.NavLinkWrapper>
          <SideNavPage.NavLinkWrapper label="Settings" icon={Icon.Settings}>
            <SideNavPage.NavLink
              href={ROUTE.EXPERT_SETTINGS_ACCOUNT()}
              label="Account"
            />
            <SideNavPage.NavLink
              href={ROUTE.EXPERT_SETTINGS_PASSWORD()}
              label="Password"
            />
          </SideNavPage.NavLinkWrapper>
          {shouldRenderExpertContent(
            EXPERT_FEATURES.EXPERT_DASHBOARD.REFERRAL_PROGRAM
          ) && (
            <SideNavPage.NavLink
              href={ROUTE.EXPERT_REFERRAL_PROGRAM()}
              label="Referral program"
              icon={Icon.ReferralProgram}
            />
          )}
        </SideNavPage.NavigationWrapper>
      )}
      popupActionButtons={() => (
        <>
          {shouldRenderExpertContent(
            EXPERT_FEATURES.EXPERT_DASHBOARD.PREVIEW_EXPERT_PROFILE
          ) && (
            <StyledLink
              icon={Icon.OpenInNew}
              variant={BUTTON_VARIANT.MINIMAL}
              href={ROUTE.EXPERT_PROFILE({ slug })}
              target="_blank"
            >
              Preview your Expert profile
            </StyledLink>
          )}
          <Button
            icon={Icon.Logout}
            variant={BUTTON_VARIANT.MINIMAL}
            onClick={signOut}
            data-test-id={DATA_TEST_ID.LOGOUT_BTN}
          >
            Log out
          </Button>
        </>
      )}
    >
      {isAdminLoggedInAsExpert && <SignedInAsUserHeader />}
      {isConsultationsCapable &&
        (!isZoomAccountActivated ||
          !(
            zoomAccountType === ZOOM_ACCOUNT_TYPE.LICENCED ||
            zoomAccountType === ZOOM_ACCOUNT_TYPE.ON_PREM
          ) ||
          !isStripeActivated) &&
        !(
          media.MOBILE &&
          LIST_OF_PAGES_WITH_FULL_VIEWPORT.includes(router.pathname)
        ) && (
          <Callout
            icon={Icon.Warning}
            intent={CALLOUT_INTENT.DANGER}
            title={(() => {
              if (!isZoomAccountActivated && !isStripeActivated) {
                return 'Set up your bank details in Stripe and Zoom account'
              }
              if (!isStripeActivated) {
                return 'Set up your bank details in Stripe'
              }
              return 'Set up your Zoom account'
            })()}
          >
            {isMain && !isStripeOnboarded && (
              <Text
                {...mobileFontSize}
                color={
                  COLOR_INTENT.EXPERT_SIDE_NAV_PAGE.COMPLETE_STRIPE_ONBOARDING
                }
              >
                {`Complete your Stripe (bank) account onboarding `}
                <LinkText
                  {...mobileFontSize}
                  onClick={async () => {
                    try {
                      const { data } = await apolloClient.query({
                        query: ExpertMeWithStripeOnboardingTokenDocument,
                        variables: { id: user.id },
                      })

                      if (data) {
                        const accountId = data.expertMe.stripeId

                        const result = await executeCreateAccountLinkMutation({
                          variables: {
                            input: {
                              accountId,
                              refreshUrl: `${environment.APP_URL}/expert`,
                              returnUrl: `${environment.APP_URL}${process.env.NEXT_PUBLIC_STRIPE_ONBOARDING_REDIRECT_URL}?state=${data.expertMe.stripeOnboardingToken}`,
                            },
                          },
                        })

                        const url = result.data?.createAccountLink

                        if (url) {
                          window.location.href = url
                        }
                      }
                    } catch (error) {
                      // Error is already displayed
                    }
                  }}
                  color={COLOR_INTENT.EXPERT_SIDE_NAV_PAGE.HERE_LINK}
                  fontWeight={FONT_WEIGHT.MEDIUM}
                  disabled={isCreateAccountLinkLoading}
                >
                  here
                </LinkText>
                .
              </Text>
            )}
            {isMain && isStripeOnboarded && !isStripeAccountActivated && (
              <Text
                {...mobileFontSize}
                color={
                  COLOR_INTENT.EXPERT_SIDE_NAV_PAGE.COMPLETE_STRIPE_ONBOARDING
                }
              >
                {`Your Stripe (bank) account requires additional details. You can get to Stripe dashboard from `}
                <LinkText
                  {...mobileFontSize}
                  onClick={() => {
                    router.push(ROUTE.EXPERT_SETTINGS_PAYMENT_OVERVIEW())
                  }}
                  color={COLOR_INTENT.EXPERT_SIDE_NAV_PAGE.PAYMENT_LINK}
                  fontWeight={FONT_WEIGHT.MEDIUM}
                >
                  Payment
                </LinkText>
                {` page.`}
              </Text>
            )}
            {!isMain && !isStripeActivated && (
              <Text
                {...mobileFontSize}
                color={
                  COLOR_INTENT.EXPERT_SIDE_NAV_PAGE.COMPLETE_STRIPE_ONBOARDING
                }
              >
                Owner of this Expert&apos;s profile has to complete Stripe
                onboarding
              </Text>
            )}
            {!isZoomAccountActivated && (
              <Text
                {...mobileFontSize}
                color={
                  COLOR_INTENT.EXPERT_SIDE_NAV_PAGE.COMPLETE_ZOOM_ONBOARDING
                }
              >
                {`Complete connecting your Zoom account. Connect Zoom by clicking on this link `}
                <LinkText
                  {...mobileFontSize}
                  onClick={() => {
                    router.push(
                      `${
                        process.env.NEXT_PUBLIC_ZOOM_AUTH_URL
                      }?${queryString.stringify({
                        response_type: 'code',
                        client_id: environment.ZOOM_CLIENT_ID,
                        redirect_uri: `${environment.APP_URL}${process.env.NEXT_PUBLIC_ZOOM_REDIRECT_URL}`,
                      })}`
                    )
                  }}
                  color={COLOR_INTENT.EXPERT_SIDE_NAV_PAGE.HERE_LINK}
                  fontWeight={FONT_WEIGHT.MEDIUM}
                >
                  here
                </LinkText>
                .
              </Text>
            )}
            {isZoomAccountActivated &&
              !(
                zoomAccountType === ZOOM_ACCOUNT_TYPE.ON_PREM ||
                zoomAccountType === ZOOM_ACCOUNT_TYPE.LICENCED
              ) && (
                <Text
                  {...mobileFontSize}
                  color={
                    COLOR_INTENT.EXPERT_SIDE_NAV_PAGE.COMPLETE_ZOOM_ONBOARDING
                  }
                >
                  {`Complete your Zoom account onboarding. TheExpert requires licenced or higher zoom user type. Update your zoom plan `}
                  <LinkText
                    {...mobileFontSize}
                    onClick={() => {
                      router.push(process.env.NEXT_PUBLIC_ZOOM_PLAN_UPGRADE_URL)
                    }}
                    color={COLOR_INTENT.EXPERT_SIDE_NAV_PAGE.HERE_LINK}
                    fontWeight={FONT_WEIGHT.MEDIUM}
                  >
                    here
                  </LinkText>
                  .
                </Text>
              )}
          </Callout>
        )}
      {children}
    </SideNavPage>
  )
}

const ExpertSideNavPageWithGA4 = withGA4Provider({
  page_area: GA4_PAGE_AREAS.EXPERT_PROFILE,
})(ExpertSideNavPage)

export default withExpertAuthorization(ExpertSideNavPageWithGA4)
